import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import MapPicker from "./MapPicker";
import { useClickOutside } from './../../Hooks/useClickOutside';

/* ── FloatInput Component (kept exactly as you had it) ───────────────────────────── */
const FloatInput = ({ label, value, onChange, maxLength, required, type = "text", className = "" }) => {
  const [focused, setFocused] = React.useState(false);
  const active = focused || (value && value.length > 0);

  return (
    <div className={`relative ${className}`}>
      <label
        className={`
          absolute left-3 transition-all duration-200 pointer-events-none z-10 bg-white px-1
          ${active ? "-top-2 text-[10px] text-primaryColor font-medium" : "top-3 text-xs text-gray-400"}
        `}
      >
        {label}{required && <span className="text-primaryColor ml-0.5">*</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        maxLength={maxLength}
        placeholder=""
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        className={`
          w-full px-3 py-3 border-2 rounded-xl outline-none text-sm text-gray-800 bg-white
          transition-all duration-200
          ${focused ? "border-primaryColor shadow-sm shadow-primaryColor/20" : "border-gray-200 hover:border-gray-300"}
        `}
      />
    </div>
  );
};

const AddressModal = ({
  showModal,
  closeModal,
  editingIndex,
  addresses,
  setAddresses,
  handleMapAddressChange,
  updateAddress,
}) => {
  const [animate, setAnimate] = useState(false);
  const [draftAddress, setDraftAddress] = useState(null);

  useEffect(() => {
    if (showModal) setTimeout(() => setAnimate(true), 10);
  }, [showModal]);

  useEffect(() => {
    if (showModal && editingIndex !== null) {
      setDraftAddress({ ...addresses[editingIndex] });
    }
  }, [showModal, editingIndex]);

  const handleClose = () => {
    setAnimate(false);
    setTimeout(closeModal, 250);
  };

  if (!showModal || editingIndex === null) return null;

  return createPortal(
    <div
      className={`fixed inset-0 z-50 flex items-end md:items-center justify-center
      bg-black/30 backdrop-blur-sm transition-opacity duration-300
      ${animate ? "opacity-100" : "opacity-0"}`}
    >
      <div
        className={`bg-white w-full md:w-[520px] md:rounded-3xl rounded-t-3xl shadow-2xl flex flex-col transition-all duration-300 overflow-hidden
        ${animate ? "scale-100 translate-y-0" : "scale-95 md:translate-y-4 translate-y-full"}`}
        style={{ maxHeight: "90vh" }}
      >
        {/* Modal Header - matches your third screenshot style */}
        <div className="px-5 py-4 bg-primaryColor text-white flex items-center rounded-t-3xl md:rounded-t-3xl">
          <button
            onClick={handleClose}
            className="text-white text-2xl leading-none"
          >
            ←
          </button>
          <div className="flex-1 text-center font-semibold text-lg">
            {editingIndex === 0 ? "Office Location" : "Warehouse Location"}
          </div>
          {/* <button
            onClick={handleClose}
            className="text-white/80 text-sm font-medium"
          >
            Skip
          </button> */}
        </div>

        {/* Modal Content */}
        <div className="flex-1 overflow-auto p-5">
          <MapPicker
            apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
            lat={draftAddress?.lat}
            lng={draftAddress?.lng}
            onAddressChange={(addrObj) =>
              setDraftAddress((prev) => ({
                ...prev,
                addressLine1: addrObj.addressLine1 || prev.addressLine1,
                city: addrObj.city || prev.city,
                state: addrObj.state || prev.state,
                pincode: addrObj.pincode || prev.pincode,
                lat: addrObj.lat ?? prev.lat,
                lng: addrObj.lng ?? prev.lng,
              }))
            }
          />

          <div className="mt-6 space-y-4">
            <FloatInput
              label="Address Line 1"
              required
              value={draftAddress?.addressLine1 || ""}
              onChange={(e) =>
                setDraftAddress((prev) => ({
                  ...prev,
                  addressLine1: e.target.value,
                }))
              }
            />
            <FloatInput
              label="Address Line 2 (optional)"
              value={draftAddress?.addressLine2 || ""}
              onChange={(e) =>
                setDraftAddress((prev) => ({
                  ...prev,
                  addressLine2: e.target.value,
                }))
              }
            />
            <div className="grid grid-cols-2 gap-3">
              <FloatInput
                label="City"
                required
                value={draftAddress?.city || ""}
                onChange={(e) =>
                  setDraftAddress((prev) => ({
                    ...prev,
                    city: e.target.value,
                  }))
                }
              />
              <FloatInput
                label="Pincode"
                required
                maxLength={6}
                value={draftAddress?.pincode || ""}
                onChange={(e) =>
                  setDraftAddress((prev) => ({
                    ...prev,
                    pincode: e.target.value.replace(/\D/g, ""),
                  }))
                }
              />
            </div>
            <FloatInput
              label="State"
              required
              value={draftAddress?.state || ""}
              onChange={(e) =>
                setDraftAddress((prev) => ({
                  ...prev,
                  state: e.target.value,
                }))
              }
            />
          </div>

          {editingIndex === 1 && addresses.length > 0 && (
            <button
              type="button"
              onClick={() => {
                const officeData = addresses[0];
                const updated = [...addresses];
                updated[1] = {
                  ...officeData,
                  addressType: "WareHouse",
                };
                setAddresses(updated);
              }}
              className="mt-6 w-full flex items-center justify-center gap-3 border-2 border-green-500 text-green-600 py-2 rounded-2xl font-medium hover:bg-green-50 transition-colors"
            >
              <span className="text-2xl">📍</span>
              Same as Office Location
            </button>
          )}
        </div>

        {/* Modal Footer */}
        <div className="p-5 border-t flex gap-3">
          <button
            onClick={handleClose}
            className="flex-1 py-3.5 border-2 border-gray-300 text-gray-700 rounded-2xl font-medium"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              const updated = [...addresses];
              updated[editingIndex] = draftAddress;
              setAddresses(updated);
              handleClose();
            }}
            className="flex-1 py-3.5 bg-primaryColor text-white rounded-2xl font-semibold"
          >
            Confirm Location
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AddressModal;